import { sanitizeFileNameForUpload } from './sanitizeFileNameForUpload';

export const UPLOAD_STALL_TIMEOUT_MS = 20000;
export const MAX_UPLOAD_ATTEMPTS = 3;

const attemptUploadToS3 = async (
    signedUrl: string,
    file: File,
    onProgressHandler?: (loaded: number) => void,
): Promise<string> =>
    new Promise<string>((resolve, reject) => {
        const filename = sanitizeFileNameForUpload(file.name);
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Disposition', `filename="${filename}"`);
        // DS-2224, DS-2469: IE11 needs this header for uploads, but giving an empty value breaks fonts in Chrome.
        if (file.type) {
            xhr.setRequestHeader('Content-Type', file.type);
        }

        let stallTimer: NodeJS.Timeout;

        const clearStallTimer = () => {
            clearTimeout(stallTimer);
        };

        const resetStallTimer = () => {
            clearStallTimer();
            stallTimer = setTimeout(() => xhr.abort(), UPLOAD_STALL_TIMEOUT_MS);
        };

        const handleResponse = () => {
            clearStallTimer();
            if (xhr.status === 200) {
                resolve(xhr.responseText);
            } else {
                const response = xhr.responseText || xhr.status || 'Upload failed';
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                reject(response);
            }
        };

        xhr.upload.onprogress = event => {
            resetStallTimer();
            if (event.lengthComputable) {
                onProgressHandler?.(event.loaded);
            }
        };
        xhr.onload = handleResponse;
        xhr.onerror = handleResponse;
        xhr.onabort = () => {
            clearStallTimer();
            reject(new Error('Upload stalled'));
        };

        resetStallTimer();
        xhr.send(file);
    });

export const uploadToS3 = async (
    signedUrl: string,
    file: File,
    onProgressHandler?: (loaded: number) => void,
): Promise<string> => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            return await attemptUploadToS3(signedUrl, file, onProgressHandler);
        } catch (error) {
            lastError = error;
        }
    }

    throw lastError;
};
